import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Heart, Loader2, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link } from "wouter";
import { currentPaywallToolId, paywallClicked } from "@/lib/paywallEvents";
import { DAY_PASS_PRICE, FREE_DAILY_USES, WORKFLOW_NOT_INCLUDED_NOTE, paywallOffer } from "@/lib/plans";
import { getOrCreateDeviceId } from "@/lib/deviceId";
import { refreshUsage, usageScope } from "@/lib/usageGate";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { openGuestCheckout } from "@/hooks/useGuestCheckout";
import { getLoginUrlForPlan } from "@/const";
import { exclusionFlags, funnelOptedOut } from "@/components/FunnelBeacon";
import { hideDailyLimitPaywall, paywallActionNoun, paywallHeading, usePaywallAction, usePaywallAtDownload, usePaywallDialogOpen, usePaywallIsPremium } from "@/hooks/usePaywallDialog";
import { trackCheckoutStart } from "@/lib/analytics";

/**
 * The clear, direct "you've hit today's free limit" moment -- shown as
 * a real, prominent dialog instead of the easy-to-miss toast this
 * replaced. Deliberately does NOT gate every use behind a paywall; it
 * only appears once someone's genuinely out of free uses for the day
 * (the 11th attempt), same free-then-paid model as before, just with a
 * clearer, more direct moment at the actual limit instead of a
 * dismissible toast pointing back up at a banner.
 *
 * Mounted once, globally, in Layout.tsx and controlled via the
 * lightweight usePaywallDialog store so any tool (file-based or
 * calculator-style) can trigger it from wherever it detects the limit,
 * without needing its own copy of this dialog or checkout logic.
 */
export function DailyLimitPaywallDialog() {
  const open = usePaywallDialogOpen();
  const { isAuthenticated } = useAuth();
  const premium = usePaywallIsPremium();
  const atDownload = usePaywallAtDownload();
  const offer = paywallOffer(premium);
  const action = usePaywallAction();
  const createCheckoutSession = trpc.billing.createCheckoutSession.useMutation();
  const createPremiumCheckout = trpc.premiumApps.checkout.useMutation();
  const [loadingPlan, setLoadingPlan] = useState<"day_pass" | "monthly" | null>(null);

  // Funnel counter: one anonymous +1 each time this dialog opens (no ID, nothing about the visitor is sent).
  const funnelHit = trpc.funnel.hit.useMutation();
  useEffect(() => {
    if (open && !funnelOptedOut()) funnelHit.mutate({ step: "paywall_view", tag: premium ? "premium_apps" : "pro", ...exclusionFlags() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Access codes: handed out by the site owner so testers (people or AI agents) can keep
  // going past the free limit without a purchase. Entirely separate from Stripe.
  const redeem = trpc.accessCodes.redeem.useMutation();
  const utils = trpc.useUtils();
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);

  const applyCode = async () => {
    if (!code.trim() || redeem.isPending) return;
    setCodeError(null);
    try {
      const deviceId = getOrCreateDeviceId();
      const result = await redeem.mutateAsync({ code, deviceId });
      // Re-read the server's answer so every tool that was blocked unblocks right away.
      await refreshUsage(usageScope(isAuthenticated, deviceId, premium), () =>
        utils.client.usage.check.query({ toolId: premium ? "document-scanner" : "general", deviceId: isAuthenticated ? undefined : deviceId }),
      );
      void utils.usage.check.invalidate();
      toast.success(`Access unlocked until ${new Date(result.expiresAt).toLocaleString()}`);
      setCode("");
      setShowCode(false);
      hideDailyLimitPaywall();
    } catch (err) {
      setCodeError(err instanceof Error ? err.message : "That code didn't work. Please try again.");
    }
  };

  const handleBuy = async (plan: "day_pass" | "monthly") => {
    paywallClicked(plan);
    if (!isAuthenticated) {
      openGuestCheckout(premium ? (plan === "day_pass" ? "premium_day_pass" : "premium_monthly") : plan);
      return;
    }
    setLoadingPlan(plan);
    try {
      const result = premium ? await createPremiumCheckout.mutateAsync({ plan }) : await createCheckoutSession.mutateAsync({ plan });
      trackCheckoutStart(plan, premium ? "premium" : "professional", currentPaywallToolId());
      window.location.href = result.url;
    } catch {
      setLoadingPlan(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && hideDailyLimitPaywall()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1" style={{ color: "var(--signal)" }}>
            <Heart className="h-5 w-5" />
            <span className="text-xs font-bold uppercase tracking-wide">We value your usage</span>
          </div>
          <DialogTitle>{paywallHeading(atDownload)}</DialogTitle>
          <DialogDescription>
            {action && `Your input stays where it is, but ${paywallActionNoun(action)} needs a pass. `}
            {premium
              ? `To keep using Premium Applications today, grab a Day Pass for ${offer.dayPass}. Either pass also unlocks every Professional Tool and removes the \u201cCreated with FreeDailyPro.com\u201d mark from your PDFs and images.`
              : `You've used today's ${FREE_DAILY_USES} trial uses. A Day Pass for ${DAY_PASS_PRICE} gives unlimited use for 24 hours, and either pass removes the \u201cCreated with FreeDailyPro.com\u201d mark from your PDFs and images.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button className="w-full gap-2" onClick={() => handleBuy("day_pass")} disabled={loadingPlan !== null}>
            {loadingPlan === "day_pass" && <Loader2 className="h-4 w-4 animate-spin" />}
            {offer.dayPassLabel}
          </Button>
          <p className="text-center text-sm font-medium pt-1" data-testid="paywall-saver-line">{offer.saverLine}</p>
          <Button variant="outline" className="w-full gap-2" onClick={() => handleBuy("monthly")} disabled={loadingPlan !== null}>
            {loadingPlan === "monthly" && <Loader2 className="h-4 w-4 animate-spin" />}
            {offer.monthlyLabel}
          </Button>
          {showCode ? (
            <div className="w-full space-y-2 pt-1" data-testid="access-code-form">
              <div className="flex gap-2">
                <Input
                  value={code}
                  onChange={(e) => { setCode(e.target.value); setCodeError(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") void applyCode(); }}
                  placeholder="Access code"
                  aria-label="Access code"
                  aria-invalid={codeError ? true : undefined}
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  autoFocus
                />
                <Button onClick={() => void applyCode()} disabled={!code.trim() || redeem.isPending}>
                  {redeem.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                </Button>
              </div>
              {codeError && <p role="alert" className="text-sm text-destructive">{codeError}</p>}
            </div>
          ) : (
            <Button variant="ghost" className="w-full gap-2 text-muted-foreground" onClick={() => setShowCode(true)}>
              <KeyRound className="h-4 w-4" /> Have an access code?
            </Button>
          )}
          <p className="text-center text-xs text-muted-foreground" data-testid="paywall-workflow-note">{WORKFLOW_NOT_INCLUDED_NOTE}</p>
          {!premium && (
            <Link
              href="/pricing"
              className="text-center text-sm underline underline-offset-2 text-muted-foreground hover:text-foreground"
              onClick={() => { paywallClicked("pricing"); hideDailyLimitPaywall(); }}
            >
              Compare plans
            </Link>
          )}
          <Button variant="ghost" className="w-full text-muted-foreground" onClick={hideDailyLimitPaywall}>
            {atDownload ? "Not now" : "Maybe tomorrow"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
